import { useEffect,useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { ACTIVE_STATUS, USERS_DEFAULT_TAKE, USERS_TYPE_NAME } from 'common/constants/reports';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserGroupsUsers } from 'common/services/api/UserGroupsUsers/userGroupsUsers.thunk';
import { UserGroup } from 'common/types/Entities';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import {
    fetchAllUsers,
    getLimitedNumberOfUniqueUsersSortedByEmail,
    getUniqueUsersSortedByEmailWithoutLimitNumber,
    isPromise,
} from '../helpers';
import {
    User,
    UserGroupsUsersPromiseList,
    UserGroupsUsersResponse } from '../typeDefs';

export const useFetchUserGroupsUsers = (userGroups: UserGroup[], userLimit: string, userLimitDisabled: boolean): {
    processing: boolean,
    users: User[]
} => {
    const [users, setUsers] = useState<User[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const numberOfUsersToFetch = userLimitDisabled || Number(userLimit) > USERS_DEFAULT_TAKE
        ? USERS_DEFAULT_TAKE : Number(userLimit);

    const _populateComputedUsersResultToState = (
        fetchedUserGroupsUsersList: UserGroupsUsersResponse[],
        userLimitProp?: number
    ) => {
        const uniqueSortedUsers = userLimitProp ?
            getLimitedNumberOfUniqueUsersSortedByEmail(fetchedUserGroupsUsersList, userLimitProp) :
            getUniqueUsersSortedByEmailWithoutLimitNumber(fetchedUserGroupsUsersList);
        setUsers(uniqueSortedUsers);
        setProcessing(false);
    };

    const _handleComputeUsersFromPromise = (
        allUserGroupsUsersResponse: Promise<UserGroupsUsersResponse[]>,
        userGroupsUsersFetchedOnFirstPageLoad: UserGroupsUsersResponse[],
        userLimitProp?: number
    ) => {
        allUserGroupsUsersResponse.then(theRestOfFetchedUsers => {
            const allFetchedUsersList = [
                ...userGroupsUsersFetchedOnFirstPageLoad,
                ...theRestOfFetchedUsers
            ];
            _populateComputedUsersResultToState(allFetchedUsersList, userLimitProp);
        })
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), USERS_TYPE_NAME, enqueueSnackbar);
                setProcessing(false);
            });
    };

    const _handleComputeFetchedUsers = (
        userGroupsUsersFetchedOnFirstPageLoad: UserGroupsUsersResponse[],
        userLimitProp?: number
    ) => {
        const fetchedUsersListPromise = fetchAllUsers(
            userGroupsUsersFetchedOnFirstPageLoad,
            USERS_DEFAULT_TAKE,
            userLimitProp
        );
        if (fetchedUsersListPromise && isPromise(fetchedUsersListPromise)) {
            _handleComputeUsersFromPromise(
                fetchedUsersListPromise,
                userGroupsUsersFetchedOnFirstPageLoad,
                userLimitProp
            );
        } else {
            _populateComputedUsersResultToState(userGroupsUsersFetchedOnFirstPageLoad, userLimitProp);
        }
    };

    const _handleComputeLimitedNumberOfUsers = (
        userGroupsUsersFetchedOnFirstPageLoad: UserGroupsUsersResponse[],
        userLimitProp: number
    ) => {
        if (userLimitProp > USERS_DEFAULT_TAKE) {
            _handleComputeFetchedUsers(userGroupsUsersFetchedOnFirstPageLoad, userLimitProp);
        } else {
            _populateComputedUsersResultToState(userGroupsUsersFetchedOnFirstPageLoad, userLimitProp);
        }
    };

    useEffect(() => {
        setProcessing(true);
        const userGroupUsersPromise = userGroups.map((userGroup) => {
            const requestParams = {
                tenantId: userGroup.tenantId,
                usergroupId: userGroup.id,
                status: [ACTIVE_STATUS],
                skip: 0,
                take: numberOfUsersToFetch
            };
            return dispatch(fetchUserGroupsUsers(requestParams))
                .unwrap()
                .catch((error) => {
                    handleListFetchError(extractStatusCode(error.message), USERS_TYPE_NAME, enqueueSnackbar);
                }) as UserGroupsUsersPromiseList;
        });

        Promise
            .all(userGroupUsersPromise)
            .then((userGroupUsers) => {
                const filteredUserGroupsUsers = userGroupUsers.filter(item => !!item);

                if (userLimitDisabled) {
                    _handleComputeFetchedUsers(filteredUserGroupsUsers);
                } else {
                    _handleComputeLimitedNumberOfUsers(filteredUserGroupsUsers, Number(userLimit));
                }
            }).catch((error) => {
                handleListFetchError(extractStatusCode(error.message), USERS_TYPE_NAME, enqueueSnackbar);
                setProcessing(false);
            });
    }, [userGroups.length, userLimitDisabled]);

    return {
        processing,
        users
    };
};
